import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Bell,
  BookOpen,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ClipboardList,
  Copy,
  Eye,
  EyeOff,
  ExternalLink,
  Globe,
  Image,
  Layers3,
  LayoutGrid,
  Link2,
  Mail,
  Megaphone,
  MonitorSmartphone,
  Package,
  Palette,
  Phone,
  QrCode,
  ReceiptText,
  Search,
  Settings2,
  ShoppingBag,
  ShoppingCart,
  Store,
  Ticket,
  TimerReset,
  Truck,
  Users,
  Wallet,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { getCategories, getCustomers, getOrders, getProducts, getRooms, getTables } from '../../../lib/api';
import {
  POS_CATEGORY_ICON_OPTIONS,
  POS_DEFAULT_TERMINAL_THEME,
  findDefaultCategoryAppearance,
} from '../../../lib/posCategoryAppearance';
import {
  buildDefaultPosHomeShortcuts,
  normalizePosHomeShortcuts,
  POS_HOME_ICON_OPTIONS,
  POS_HOME_ROUTE_OPTIONS,
  resolvePosHomeIcon,
} from '../../../lib/posHomeScreen';
import { buildWebshopUrl, sanitizeWebshopSlug, useManagerPortalConfig } from './useManagerPortalConfig';

function createId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

function formatMoney(value) {
  return new Intl.NumberFormat('de-AT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatDateTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('de-AT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

function sourceLabel(source) {
  switch (source) {
    case 'foodora':
      return 'Foodora';
    case 'lieferando':
      return 'Lieferando';
    case 'website':
      return 'Webshop';
    case 'wolt':
      return 'Wolt';
    default:
      return source || 'POS';
  }
}

function statusTone(status) {
  switch ((status || '').toLowerCase()) {
    case 'completed':
      return 'bg-emerald-50 text-emerald-700';
    case 'cancelled':
      return 'bg-rose-50 text-rose-700';
    case 'missed':
      return 'bg-amber-50 text-amber-700';
    default:
      return 'bg-slate-100 text-slate-600';
  }
}

function PageHero({ title, description, actions, eyebrow = 'Manager Portal' }) {
  return (
    <div className="flex flex-col gap-4 px-8 py-8 xl:flex-row xl:items-start xl:justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{eyebrow}</p>
        <h1 className="mt-2 text-5xl font-bold tracking-tight text-[#132d39]">{title}</h1>
        <p className="mt-3 max-w-3xl text-base text-slate-500">{description}</p>
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-3">{actions}</div> : null}
    </div>
  );
}

function LoadingView() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="rounded-3xl border border-slate-200 bg-white px-6 py-5 text-sm text-slate-500 shadow-sm">
        Manager Portal wird vorbereitet…
      </div>
    </div>
  );
}

function StatCard({ label, value, hint, accent = 'bg-[#71c545]' }) {
  return (
    <Card className="rounded-[28px] border-slate-200 bg-white shadow-sm">
      <CardContent className="p-5">
        <div className={`mb-4 h-1.5 w-16 rounded-full ${accent}`} />
        <p className="text-sm font-medium text-slate-500">{label}</p>
        <p className="mt-3 text-4xl font-bold tracking-tight text-[#132d39]">{value}</p>
        {hint ? <p className="mt-2 text-sm text-slate-500">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

function SecondaryNav({ items, active, onChange }) {
  return (
    <div className="space-y-2">
      {items.map((item) => {
        const Icon = item.icon;
        const isActive = item.id === active;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onChange(item.id)}
            className={`flex w-full items-center gap-3 rounded-[24px] px-5 py-4 text-left transition-colors ${
              isActive ? 'bg-[#d8ebe0] text-[#132d39]' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Icon className="h-5 w-5 shrink-0" />
            <span className="font-medium">{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function Panel({ title, description, children }) {
  return (
    <Card className="rounded-[32px] border-slate-200 bg-white shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="text-[2rem] font-bold tracking-tight text-[#314b59]">{title}</CardTitle>
        {description ? <p className="text-sm text-slate-500">{description}</p> : null}
      </CardHeader>
      <CardContent className="space-y-5">{children}</CardContent>
    </Card>
  );
}

function FormField({ label, hint, children }) {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      {children}
      {hint ? <p className="text-xs text-slate-400">{hint}</p> : null}
    </div>
  );
}

function ToggleRow({ label, description, checked, onChange }) {
  return (
    <label className="flex items-start gap-4 rounded-[24px] border border-slate-200 p-4">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1 h-5 w-5 rounded border-slate-300 text-[#71c545] focus:ring-[#71c545]"
      />
      <div>
        <p className="font-medium text-[#132d39]">{label}</p>
        {description ? <p className="mt-1 text-sm text-slate-500">{description}</p> : null}
      </div>
    </label>
  );
}

function SaveActions({ onSave, secondary }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {secondary}
      <Button onClick={onSave} className="rounded-2xl bg-[#71c545] px-6 text-white hover:bg-[#63b13b]">
        Speichern
      </Button>
    </div>
  );
}

export function AdminLocationPresence() {
  const { portalConfig, settings, isLoading, savePortalConfig, saveSettingsPatch, weekdays } = useManagerPortalConfig();
  const [tab, setTab] = useState('details');
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (portalConfig?.locationPresence) {
      setDraft(portalConfig.locationPresence);
    }
  }, [portalConfig]);

  if (isLoading || !draft) return <LoadingView />;

  const tabs = [
    { id: 'details', label: 'Details', icon: Store },
    { id: 'social', label: 'Social Media', icon: Megaphone },
    { id: 'opening', label: 'Öffnungszeiten', icon: Calendar },
    { id: 'legal', label: 'Impressum', icon: BookOpen },
    { id: 'invoice', label: 'Rechnung', icon: ReceiptText },
  ];

  const save = async () => {
    savePortalConfig((prev) => ({ ...prev, locationPresence: draft }), '');
    await saveSettingsPatch(
      {
        company_name: draft.locationName,
        company_address: draft.address,
        company_uid: draft.invoice.taxNumber,
      },
      'Standort-Stammdaten synchronisiert',
    );
    toast.success('Location presence gespeichert');
  };

  const openingHours = portalConfig?.orderSettings?.openingHours || {};

  return (
    <div className="pb-8">
      <PageHero
        title="Location presence"
        description="Pflege die öffentlichen Lokal- und App-Informationen, die in Bessa, Webshop und zukünftigen Kunden-Touchpoints sichtbar sind."
        actions={<SaveActions onSave={save} />}
      />

      <div className="grid gap-6 px-8 xl:grid-cols-[1.15fr_2.35fr]">
        <Card className="rounded-[32px] border-slate-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <SecondaryNav items={tabs} active={tab} onChange={setTab} />
          </CardContent>
        </Card>

        <Panel
          title={tabs.find((item) => item.id === tab)?.label || 'Details'}
          description="Die Struktur folgt dem Bessa Location-Profil und ist auf Webshop, App und Online-Ordering vorbereitet."
        >
          {tab === 'details' && (
            <div className="grid gap-5 md:grid-cols-2">
              <FormField label="Lokal Name">
                <Input value={draft.locationName} onChange={(e) => setDraft({ ...draft, locationName: e.target.value })} />
              </FormField>
              <FormField label="E-Mail">
                <Input value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
              </FormField>
              <div className="md:col-span-2">
                <FormField label="Über uns">
                  <Textarea
                    value={draft.about}
                    onChange={(e) => setDraft({ ...draft, about: e.target.value })}
                    className="min-h-[140px]"
                  />
                </FormField>
              </div>
              <FormField label="Adresse">
                <Input value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
              </FormField>
              <FormField label="Telefonnummer">
                <Input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
              </FormField>
              <div className="md:col-span-2">
                <FormField label="Website">
                  <Input value={draft.website} onChange={(e) => setDraft({ ...draft, website: e.target.value })} />
                </FormField>
              </div>
            </div>
          )}

          {tab === 'social' && (
            <div className="grid gap-5 md:grid-cols-2">
              <FormField label="Instagram">
                <Input
                  value={draft.social.instagram}
                  onChange={(e) => setDraft({ ...draft, social: { ...draft.social, instagram: e.target.value } })}
                />
              </FormField>
              <FormField label="Facebook">
                <Input
                  value={draft.social.facebook}
                  onChange={(e) => setDraft({ ...draft, social: { ...draft.social, facebook: e.target.value } })}
                />
              </FormField>
              <div className="md:col-span-2">
                <FormField label="TikTok">
                  <Input
                    value={draft.social.tiktok}
                    onChange={(e) => setDraft({ ...draft, social: { ...draft.social, tiktok: e.target.value } })}
                  />
                </FormField>
              </div>
            </div>
          )}

          {tab === 'opening' && (
            <div className="space-y-4">
              {weekdays.map((day) => {
                const entry = openingHours[day];
                return (
                  <div key={day} className="rounded-[24px] border border-slate-200 px-5 py-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="font-semibold capitalize text-[#132d39]">{day}</p>
                        <p className="text-sm text-slate-500">
                          {entry?.enabled
                            ? entry.slots.map((slot) => slot.join('–')).join(', ')
                            : 'Geschlossen'}
                        </p>
                      </div>
                      <div className={`rounded-full px-3 py-1 text-xs font-semibold ${entry?.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {entry?.enabled ? 'Aktiv' : 'Inaktiv'}
                      </div>
                    </div>
                  </div>
                );
              })}
              <p className="text-sm text-slate-500">
                Diese Zeiten kommen aus den Online-Order-Settings und halten den öffentlichen Auftritt und das Bestellsystem konsistent.
              </p>
            </div>
          )}

          {tab === 'legal' && (
            <div className="space-y-5">
              <FormField label="Impressum">
                <Textarea
                  value={draft.legal.imprint}
                  onChange={(e) => setDraft({ ...draft, legal: { ...draft.legal, imprint: e.target.value } })}
                  className="min-h-[160px]"
                />
              </FormField>
              <FormField label="Datenschutz">
                <Textarea
                  value={draft.legal.privacy}
                  onChange={(e) => setDraft({ ...draft, legal: { ...draft.legal, privacy: e.target.value } })}
                  className="min-h-[160px]"
                />
              </FormField>
            </div>
          )}

          {tab === 'invoice' && (
            <div className="grid gap-5 md:grid-cols-2">
              <FormField label="Firmenbuchnummer">
                <Input
                  value={draft.invoice.registrationNumber}
                  onChange={(e) => setDraft({ ...draft, invoice: { ...draft.invoice, registrationNumber: e.target.value } })}
                />
              </FormField>
              <FormField label="Steuernummer / UID">
                <Input
                  value={draft.invoice.taxNumber}
                  onChange={(e) => setDraft({ ...draft, invoice: { ...draft.invoice, taxNumber: e.target.value } })}
                />
              </FormField>
              <div className="md:col-span-2 rounded-[24px] border border-dashed border-slate-200 px-5 py-4 text-sm text-slate-500">
                Aktuelle Kassa-Stammdaten: <span className="font-semibold text-[#132d39]">{settings?.company_name || '—'}</span>
              </div>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

export function AdminAppCustomersHub() {
  const navigate = useNavigate();
  const { portalConfig } = useManagerPortalConfig();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let mounted = true;
    getCustomers()
      .then((data) => {
        if (mounted) setCustomers(data || []);
      })
      .catch(() => {
        if (mounted) setCustomers([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = customers.filter((customer) =>
    `${customer.name || ''} ${customer.email || ''} ${customer.phone || ''}`.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="pb-8">
      <PageHero
        title="App customers"
        description="Vereint CRM-Sicht, Loyalty-Einstieg und Newsletter-Opt-in rund um die Kundenbasis aus Kassa, Webshop und App."
        actions={
          <Button onClick={() => navigate('/admin/customers')} className="rounded-2xl bg-[#132d39] text-white hover:bg-[#1d3c4a]">
            Kundenverwaltung öffnen
          </Button>
        }
      />

      <div className="grid gap-4 px-8 md:grid-cols-3">
        <StatCard label="Aktive Kunden" value={customers.length} hint="Bereits in der Kassa vorhanden" />
        <StatCard label="Suchtreffer" value={filtered.length} hint="Gefilterte CRM-Kontakte" accent="bg-[#5f8dd3]" />
        <StatCard
          label="Playbooks"
          value={portalConfig?.appCustomers?.crmPlaybooks?.length || 0}
          hint="Marketing- und Retention-Vorlagen"
          accent="bg-[#f19a39]"
        />
      </div>

      <div className="grid gap-6 px-8 pt-6 xl:grid-cols-[1.3fr_1fr]">
        <Panel title="CRM Übersicht" description="Kundenlisten, Suchzugriff und die Verknüpfung in bestehende Admin-Seiten.">
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" placeholder="Search customers" />
          </div>
          <div className="space-y-3">
            {filtered.slice(0, 8).map((customer) => (
              <div key={customer.id} className="rounded-[24px] border border-slate-200 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold text-[#132d39]">{customer.name}</p>
                    <p className="mt-1 text-sm text-slate-500">{customer.email || customer.phone || 'Kein Kontakt gepflegt'}</p>
                  </div>
                  <div className={`rounded-full px-3 py-1 text-xs font-semibold ${customer.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {customer.is_active ? 'Aktiv' : 'Inaktiv'}
                  </div>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="rounded-[24px] border border-dashed border-slate-200 px-6 py-10 text-center text-slate-400">
                Keine passenden App-Kunden gefunden.
              </div>
            )}
          </div>
        </Panel>

        <Panel title="CRM Playbooks" description="Die neue Bessa-orientierte App-Kundenebene für Push, Loyalty und Wiederkehr.">
          {(portalConfig?.appCustomers?.crmPlaybooks || []).map((item) => (
            <div key={item} className="rounded-[24px] border border-slate-200 px-5 py-4 text-sm text-slate-600">
              {item}
            </div>
          ))}
        </Panel>
      </div>
    </div>
  );
}

export function AdminSetupStatus() {
  const { portalConfig } = useManagerPortalConfig();
  const [stats, setStats] = useState({ categories: [], products: [], orders: [] });

  useEffect(() => {
    let mounted = true;
    Promise.all([getCategories().catch(() => []), getProducts().catch(() => []), getOrders().catch(() => [])]).then(
      ([categories, products, orders]) => {
        if (mounted) setStats({ categories, products, orders });
      },
    );
    return () => {
      mounted = false;
    };
  }, []);

  const partnerActive =
    portalConfig?.orderSettings?.thirdParty?.foodora?.enabled || portalConfig?.orderSettings?.thirdParty?.lieferando?.enabled;
  const webOrdersReady = (stats.categories.length > 0 && stats.products.length > 0) || false;
  const activeModules = [webOrdersReady, partnerActive].filter(Boolean).length;

  const menuCount =
    portalConfig?.salesCatalog?.menus?.filter((menu) => menu.enabled !== false)?.length
    || portalConfig?.menus?.activeMenus?.filter((menu) => menu.visible)?.length
    || 0;

  return (
    <div className="pb-8">
      <PageHero
        title="Setup status"
        description="Die neue Online-Ordering-Kontrollzentrale zeigt wie in dieKassa/Bessa, ob Webshop, Menüs und Delivery-Partner wirklich produktionsbereit sind."
      />

      <div className="grid gap-4 px-8 md:grid-cols-3">
        <StatCard label="Aktive Module" value={activeModules} hint="Webshop und Delivery-Integrationen" />
        <StatCard label="Konfigurierte Kategorien" value={stats.categories.length} hint="Artikelbasis für Menüs" accent="bg-[#5f8dd3]" />
        <StatCard label="Aktive Menüs" value={menuCount} hint="Sichtbar im Online Ordering" accent="bg-[#f19a39]" />
      </div>

      <div className="grid gap-6 px-8 pt-6 xl:grid-cols-2">
        <Panel title="Web orders" description="Prüft die Kernbausteine, die im Webshop und in der Bessa-App sichtbar sind.">
          <div className="space-y-4 rounded-[24px] border border-slate-200 p-5">
            {[
              `You have ${stats.products.length} articles configured.`,
              `You have ${stats.categories.length} categories configured.`,
              `You have ${menuCount} menu(s) configured.`,
              `Webshop URL is set to ${portalConfig?.webshop?.currentUrl || '—'}.`,
              `Newsletter opt-in is ${portalConfig?.webshop?.showNewsletterOptIn ? 'enabled' : 'disabled'}.`,
            ].map((item) => (
              <div key={item} className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-[#71c545]" />
                <p className="text-sm text-slate-600">{item}</p>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Delivery partner integrations" description="Foodora und Lieferando werden auf denselben Betriebsstand wie der Webshop gebracht.">
          <div className="space-y-4 rounded-[24px] border border-slate-200 p-5">
            {[
              `Foodora is ${portalConfig?.orderSettings?.thirdParty?.foodora?.status || 'Not connected'}.`,
              `Lieferando is ${portalConfig?.orderSettings?.thirdParty?.lieferando?.status || 'Not connected'}.`,
              `Partner article mappings: ${(portalConfig?.orderSettings?.articleMappings || []).length}.`,
              `Auto accept is ${portalConfig?.orderSettings?.thirdParty?.foodora?.autoAccept ? 'enabled' : 'disabled'} for Foodora.`,
              `Cash register sync is ${portalConfig?.orderSettings?.cashRegisterConfig?.autoSyncCompletedOrders ? 'enabled' : 'disabled'}.`,
            ].map((item) => (
              <div key={item} className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-[#71c545]" />
                <p className="text-sm text-slate-600">{item}</p>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

export function AdminOnlineOrdersHub() {
  const navigate = useNavigate();
  const { portalConfig } = useManagerPortalConfig();
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    let mounted = true;
    getOrders()
      .then((data) => {
        if (mounted) setOrders(data || []);
      })
      .catch(() => {
        if (mounted) setOrders([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const onlineOrders = orders.filter((order) => ['website', 'foodora', 'lieferando', 'wolt'].includes(order.order_source));
  const filteredOrders = onlineOrders.filter((order) => {
    const matchesSource = sourceFilter === 'all' || order.order_source === sourceFilter;
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const haystack = `${order.order_number || ''} ${order.customer_name || ''} ${order.table_name || ''}`.toLowerCase();
    const matchesSearch = haystack.includes(search.toLowerCase());
    return matchesSource && matchesStatus && matchesSearch;
  });

  const successful = onlineOrders.filter((order) => order.status === 'completed');
  const cancelled = onlineOrders.filter((order) => order.status === 'cancelled');
  const missed = onlineOrders.filter((order) => order.status === 'missed');

  const partnerCards = [
    { key: 'foodora', label: 'Foodora', icon: ShoppingBag },
    { key: 'lieferando', label: 'Lieferando', icon: ShoppingCart },
    { key: 'webshop', label: 'Webshop', icon: Globe },
  ];

  return (
    <div className="pb-8">
      <PageHero
        title="Online Orders"
        description="Zentrale für Webshop, Foodora und Lieferando. Die Liste verbindet echte Kassa-Bestellungen mit den neuen Connector- und Partner-Konfigurationen."
        actions={
          <Button onClick={() => navigate('/admin/online-ordering/order-settings?section=partners')} className="rounded-2xl bg-[#71c545] text-white hover:bg-[#63b13b]">
            Partner konfigurieren
          </Button>
        }
      />

      <div className="grid gap-4 px-8 md:grid-cols-3">
        <StatCard label="Successful" value={successful.length} hint={formatMoney(successful.reduce((sum, order) => sum + Number(order.total || 0), 0))} />
        <StatCard label="Cancelled" value={cancelled.length} hint={formatMoney(cancelled.reduce((sum, order) => sum + Number(order.total || 0), 0))} accent="bg-rose-400" />
        <StatCard label="Missed" value={missed.length} hint={formatMoney(missed.reduce((sum, order) => sum + Number(order.total || 0), 0))} accent="bg-[#5f8dd3]" />
      </div>

      <div className="grid gap-4 px-8 pt-6 lg:grid-cols-3">
        {partnerCards.map((partner) => {
          const config = portalConfig?.orderSettings?.thirdParty?.[partner.key] || {};
          const Icon = partner.icon;
          return (
            <Card key={partner.key} className="rounded-[28px] border-slate-200 bg-white shadow-sm">
              <CardContent className="flex items-start gap-4 p-5">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-[#132d39]">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold text-[#132d39]">{partner.label}</p>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${config.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {config.status || 'Inactive'}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-slate-500">{config.storeId || 'Kein Store verknüpft'}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="px-8 pt-6">
        <Panel title="Order stream" description="Filterbar wie im Live-Manager und vorbereitet für Foodora/Lieferando/Webshop-Operations.">
          <div className="grid gap-4 lg:grid-cols-[1.4fr_0.6fr_0.6fr]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" placeholder="Search orders" />
            </div>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="h-11 rounded-xl border border-slate-200 px-4">
              <option value="all">Alle Quellen</option>
              <option value="website">Webshop</option>
              <option value="foodora">Foodora</option>
              <option value="lieferando">Lieferando</option>
              <option value="wolt">Wolt</option>
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-11 rounded-xl border border-slate-200 px-4">
              <option value="all">Alle Stati</option>
              <option value="completed">Completed</option>
              <option value="open">Open</option>
              <option value="cancelled">Cancelled</option>
              <option value="missed">Missed</option>
            </select>
          </div>

          <div className="overflow-hidden rounded-[24px] border border-slate-200">
            <div className="grid grid-cols-[1fr_0.85fr_0.75fr_0.75fr_0.95fr] gap-4 border-b border-slate-200 bg-slate-50 px-5 py-4 text-sm font-medium text-slate-500">
              <span>Bestellung</span>
              <span>Quelle</span>
              <span>Status</span>
              <span className="text-right">Total</span>
              <span className="text-right">Zeit</span>
            </div>
            {filteredOrders.slice(0, 12).map((order) => (
              <div key={order.id} className="grid grid-cols-[1fr_0.85fr_0.75fr_0.75fr_0.95fr] gap-4 border-b border-slate-100 px-5 py-4 text-sm last:border-0">
                <div>
                  <p className="font-semibold text-[#132d39]">{order.order_number || order.id}</p>
                  <p className="mt-1 text-slate-500">{order.customer_name || order.table_name || 'Online guest'}</p>
                </div>
                <span className="text-slate-600">{sourceLabel(order.order_source)}</span>
                <span className={`inline-flex h-fit w-fit rounded-full px-3 py-1 text-xs font-semibold ${statusTone(order.status)}`}>
                  {order.status}
                </span>
                <span className="text-right font-semibold text-[#132d39]">{formatMoney(order.total)}</span>
                <span className="text-right text-slate-500">{formatDateTime(order.created_at)}</span>
              </div>
            ))}
            {filteredOrders.length === 0 && (
              <div className="px-6 py-14 text-center text-slate-400">Noch keine passenden Online-Bestellungen.</div>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}

export function AdminMenuHub({ mode = 'menus' }) {
  const title = mode === 'daily' ? 'Daily menu' : 'Menus';
  const description =
    mode === 'daily'
      ? 'Planbare Tageskarten und Mittagsangebote auf Basis deiner vorhandenen Produkte.'
      : 'Menü- und Kategorienlogik für Webshop, Pickup und Delivery-Partner.';
  const { portalConfig, isLoading, savePortalConfig } = useManagerPortalConfig();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [draftMenus, setDraftMenus] = useState(null);
  const [draftDaily, setDraftDaily] = useState(null);

  useEffect(() => {
    Promise.all([getCategories().catch(() => []), getProducts().catch(() => [])]).then(([cats, prods]) => {
      setCategories(cats || []);
      setProducts(prods || []);
    });
  }, []);

  useEffect(() => {
    if (portalConfig?.menus) setDraftMenus(portalConfig.menus);
    if (portalConfig?.dailyMenu) setDraftDaily(portalConfig.dailyMenu);
  }, [portalConfig]);

  if (isLoading || !draftMenus || !draftDaily) return <LoadingView />;

  const save = () => {
    savePortalConfig((prev) => ({
      ...prev,
      menus: draftMenus,
      dailyMenu: draftDaily,
    }));
  };

  return (
    <div className="pb-8">
      <PageHero title={title} description={description} actions={<SaveActions onSave={save} />} />

      {mode === 'menus' ? (
        <div className="grid gap-6 px-8 xl:grid-cols-[1.15fr_1fr]">
          <Panel title="Active menus" description="Nach Order Type segmentiert wie im Bessa-/dieKassa-Online-Ordering.">
            <div className="space-y-4">
              {draftMenus.activeMenus.map((menu, index) => (
                <div key={menu.id} className="rounded-[24px] border border-slate-200 p-4">
                  <div className="grid gap-4 md:grid-cols-[1.5fr_0.7fr_0.5fr]">
                    <Input
                      value={menu.name}
                      onChange={(e) =>
                        setDraftMenus({
                          ...draftMenus,
                          activeMenus: draftMenus.activeMenus.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, name: e.target.value } : item,
                          ),
                        })
                      }
                    />
                    <select
                      value={menu.type}
                      onChange={(e) =>
                        setDraftMenus({
                          ...draftMenus,
                          activeMenus: draftMenus.activeMenus.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, type: e.target.value } : item,
                          ),
                        })
                      }
                      className="h-11 rounded-xl border border-slate-200 px-4"
                    >
                      <option value="all">All</option>
                      <option value="delivery">Delivery</option>
                      <option value="pickup">Pickup</option>
                      <option value="dine-in">Dine-in</option>
                    </select>
                    <label className="flex items-center gap-3 rounded-xl border border-slate-200 px-4">
                      <input
                        type="checkbox"
                        checked={menu.visible}
                        onChange={(e) =>
                          setDraftMenus({
                            ...draftMenus,
                            activeMenus: draftMenus.activeMenus.map((item, itemIndex) =>
                              itemIndex === index ? { ...item, visible: e.target.checked } : item,
                            ),
                          })
                        }
                      />
                      <span className="text-sm font-medium text-slate-600">Visible</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              className="rounded-2xl"
              onClick={() =>
                setDraftMenus({
                  ...draftMenus,
                  activeMenus: [
                    ...draftMenus.activeMenus,
                    { id: createId('menu'), name: 'New menu', type: 'all', visible: true, categoryIds: [] },
                  ],
                })
              }
            >
              Menü hinzufügen
            </Button>
          </Panel>

          <Panel title="Category coverage" description="Zeigt welche Warengruppen im Schnellzugriff beziehungsweise Hauptscreen hervorgehoben werden.">
            <div className="space-y-3">
              {categories.map((category) => (
                <div key={category.id} className="rounded-[24px] border border-slate-200 px-5 py-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="font-semibold text-[#132d39]">{category.name}</p>
                      <p className="mt-1 text-sm text-slate-500">
                        {products.filter((product) => product.category_id === category.id).length} Artikel
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        checked={draftMenus.featuredCategoryIds.includes(category.id)}
                        onChange={(e) =>
                          setDraftMenus({
                            ...draftMenus,
                            featuredCategoryIds: e.target.checked
                              ? [...draftMenus.featuredCategoryIds, category.id]
                              : draftMenus.featuredCategoryIds.filter((id) => id !== category.id),
                          })
                        }
                      />
                      Schnellzugriff
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      ) : (
        <div className="grid gap-6 px-8 xl:grid-cols-[1fr_1.35fr]">
          <Panel title="Daily menu profile" description="Headline und Angebotslogik für das tägliche Menü.">
            <FormField label="Headline">
              <Input value={draftDaily.headline} onChange={(e) => setDraftDaily({ ...draftDaily, headline: e.target.value })} />
            </FormField>
            <FormField label="Subline">
              <Textarea value={draftDaily.subline} onChange={(e) => setDraftDaily({ ...draftDaily, subline: e.target.value })} />
            </FormField>
          </Panel>
          <Panel title="Weekly planning" description="Jeder Wochentag kann separat für das Tagesmenü aktiviert werden.">
            <div className="space-y-4">
              {Object.entries(draftDaily.days).map(([day, entry]) => (
                <div key={day} className="rounded-[24px] border border-slate-200 p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="font-semibold capitalize text-[#132d39]">{day}</p>
                      <p className="mt-1 text-sm text-slate-500">{entry.productIds.length} Produkt(e) ausgewählt</p>
                    </div>
                    <label className="flex items-center gap-3 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        checked={entry.enabled}
                        onChange={(e) =>
                          setDraftDaily({
                            ...draftDaily,
                            days: {
                              ...draftDaily.days,
                              [day]: { ...entry, enabled: e.target.checked },
                            },
                          })
                        }
                      />
                      Aktiv
                    </label>
                  </div>
                  <Input
                    className="mt-4"
                    value={entry.title}
                    onChange={(e) =>
                      setDraftDaily({
                        ...draftDaily,
                        days: {
                          ...draftDaily.days,
                          [day]: { ...entry, title: e.target.value },
                        },
                      })
                    }
                    placeholder="Titel für diesen Tag"
                  />
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}

export function AdminOrderSettings() {
  const { portalConfig, isLoading, savePortalConfig, saveSettingsPatch } = useManagerPortalConfig();
  const [rooms, setRooms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [draft, setDraft] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get('section') || 'opening-hours';

  useEffect(() => {
    Promise.all([getRooms().catch(() => []), getCategories().catch(() => [])]).then(([roomData, categoryData]) => {
      setRooms(roomData || []);
      setCategories(categoryData || []);
    });
  }, []);

  useEffect(() => {
    if (portalConfig?.orderSettings) {
      setDraft(portalConfig.orderSettings);
    }
  }, [portalConfig]);

  if (isLoading || !draft) return <LoadingView />;

  const items = [
    { id: 'opening-hours', label: 'Opening hours', icon: Calendar },
    { id: 'order-times', label: 'Order times', icon: TimerReset },
    { id: 'closures', label: 'Closures', icon: Calendar },
    { id: 'order-types', label: 'Order types', icon: Settings2 },
    { id: 'delivery-fees', label: 'Delivery fees', icon: Truck },
    { id: 'partners', label: 'Third-party services', icon: ShoppingBag },
    { id: 'article-mappings', label: 'Article mappings', icon: Link2 },
    { id: 'cash-register', label: 'Cash register config', icon: Wallet },
    { id: 'payment', label: 'Payment', icon: ReceiptText },
    { id: 'tables-qr', label: 'Tables & QR', icon: QrCode },
    { id: 'order-sources', label: 'Order sources', icon: ClipboardList },
    { id: 'general', label: 'General', icon: Settings2 },
  ];

  const save = async () => {
    savePortalConfig((prev) => ({ ...prev, orderSettings: draft }), '');
    await saveSettingsPatch(
      {
        terminal_id: draft.cashRegisterConfig.terminalId,
        kassen_id: draft.cashRegisterConfig.kassenId,
      },
      'Terminaldaten synchronisiert',
    );
    toast.success('Order Settings gespeichert');
  };

  const openingHoursSection = (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-3">
        <Button
          variant="outline"
          className="rounded-2xl"
          onClick={() =>
            setDraft({
              ...draft,
              openingHours: {
                ...draft.openingHours,
                monday: { enabled: true, slots: [['09:00', '17:00']] },
                tuesday: { enabled: true, slots: [['09:00', '17:00']] },
                wednesday: { enabled: true, slots: [['09:00', '17:00']] },
                thursday: { enabled: true, slots: [['09:00', '17:00']] },
                friday: { enabled: true, slots: [['09:00', '17:00']] },
              },
            })
          }
        >
          Mon–Fri 9–5
        </Button>
        <Button
          variant="outline"
          className="rounded-2xl"
          onClick={() =>
            setDraft({
              ...draft,
              openingHours: Object.fromEntries(
                Object.entries(draft.openingHours).map(([day, entry]) => [day, { ...entry, enabled: true, slots: [['11:00', '14:00'], ['17:00', '22:00']] }]),
              ),
            })
          }
        >
          Lunch + dinner
        </Button>
        <Button
          variant="outline"
          className="rounded-2xl"
          onClick={() =>
            setDraft({
              ...draft,
              openingHours: Object.fromEntries(
                Object.entries(draft.openingHours).map(([day]) => [day, { enabled: true, slots: [['00:00', '23:59']] }]),
              ),
            })
          }
        >
          Open 24/7
        </Button>
      </div>

      {Object.entries(draft.openingHours).map(([day, entry]) => (
        <div key={day} className="rounded-[24px] border border-slate-200 p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold capitalize text-[#132d39]">{day}</p>
              <p className="mt-1 text-sm text-slate-500">{entry.enabled ? 'Online ordering is open' : 'Closed'}</p>
            </div>
            <label className="flex items-center gap-3 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={entry.enabled}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    openingHours: {
                      ...draft.openingHours,
                      [day]: { ...entry, enabled: e.target.checked },
                    },
                  })
                }
              />
              Aktiv
            </label>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <Input
              value={entry.slots[0]?.[0] || ''}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  openingHours: {
                    ...draft.openingHours,
                    [day]: {
                      ...entry,
                      slots: [[e.target.value, entry.slots[0]?.[1] || ''], entry.slots[1] || ['', '']],
                    },
                  },
                })
              }
            />
            <Input
              value={entry.slots[0]?.[1] || ''}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  openingHours: {
                    ...draft.openingHours,
                    [day]: {
                      ...entry,
                      slots: [[entry.slots[0]?.[0] || '', e.target.value], entry.slots[1] || ['', '']],
                    },
                  },
                })
              }
            />
            <Input
              value={entry.slots[1]?.[0] || ''}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  openingHours: {
                    ...draft.openingHours,
                    [day]: {
                      ...entry,
                      slots: [entry.slots[0] || ['', ''], [e.target.value, entry.slots[1]?.[1] || '']],
                    },
                  },
                })
              }
            />
            <Input
              value={entry.slots[1]?.[1] || ''}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  openingHours: {
                    ...draft.openingHours,
                    [day]: {
                      ...entry,
                      slots: [entry.slots[0] || ['', ''], [entry.slots[1]?.[0] || '', e.target.value]],
                    },
                  },
                })
              }
            />
          </div>
        </div>
      ))}
    </div>
  );

  const contentBySection = {
    'opening-hours': openingHoursSection,
    'order-times': (
      <div className="grid gap-5 md:grid-cols-2">
        <FormField label="Pickup lead time (min)">
          <Input
            type="number"
            value={draft.orderTimes.pickupLeadMinutes}
            onChange={(e) => setDraft({ ...draft, orderTimes: { ...draft.orderTimes, pickupLeadMinutes: Number(e.target.value) } })}
          />
        </FormField>
        <FormField label="Delivery lead time (min)">
          <Input
            type="number"
            value={draft.orderTimes.deliveryLeadMinutes}
            onChange={(e) => setDraft({ ...draft, orderTimes: { ...draft.orderTimes, deliveryLeadMinutes: Number(e.target.value) } })}
          />
        </FormField>
        <FormField label="Preorder days">
          <Input
            type="number"
            value={draft.orderTimes.preorderDays}
            onChange={(e) => setDraft({ ...draft, orderTimes: { ...draft.orderTimes, preorderDays: Number(e.target.value) } })}
          />
        </FormField>
        <FormField label="Slot interval (min)">
          <Input
            type="number"
            value={draft.orderTimes.slotIntervalMinutes}
            onChange={(e) => setDraft({ ...draft, orderTimes: { ...draft.orderTimes, slotIntervalMinutes: Number(e.target.value) } })}
          />
        </FormField>
      </div>
    ),
    closures: (
      <div className="space-y-4">
        {draft.closures.map((closure, index) => (
          <div key={closure.id} className="grid gap-3 md:grid-cols-[0.8fr_1.2fr_auto]">
            <Input
              type="date"
              value={closure.date}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  closures: draft.closures.map((item, itemIndex) => (itemIndex === index ? { ...item, date: e.target.value } : item)),
                })
              }
            />
            <Input
              value={closure.label}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  closures: draft.closures.map((item, itemIndex) => (itemIndex === index ? { ...item, label: e.target.value } : item)),
                })
              }
              placeholder="Feiertag oder Sonderhinweis"
            />
            <Button
              variant="outline"
              onClick={() => setDraft({ ...draft, closures: draft.closures.filter((_, itemIndex) => itemIndex !== index) })}
            >
              Entfernen
            </Button>
          </div>
        ))}
        <Button
          variant="outline"
          className="rounded-2xl"
          onClick={() => setDraft({ ...draft, closures: [...draft.closures, { id: createId('closure'), date: '', label: '' }] })}
        >
          Closure hinzufügen
        </Button>
      </div>
    ),
    'order-types': (
      <div className="space-y-4">
        {[
          ['pickup', 'Pickup'],
          ['delivery', 'Delivery'],
          ['dineIn', 'Dine-in'],
          ['scheduled', 'Scheduled orders'],
        ].map(([key, label]) => (
          <ToggleRow
            key={key}
            label={label}
            description="Aktiviert diese Bestellart im Webshop und in den Delivery-Connectors."
            checked={draft.orderTypes[key]}
            onChange={(checked) => setDraft({ ...draft, orderTypes: { ...draft.orderTypes, [key]: checked } })}
          />
        ))}
      </div>
    ),
    'delivery-fees': (
      <div className="space-y-4">
        {draft.deliveryFees.map((fee, index) => (
          <div key={fee.id} className="grid gap-3 md:grid-cols-[1.2fr_0.6fr_0.6fr_auto]">
            <Input
              value={fee.zone}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  deliveryFees: draft.deliveryFees.map((item, itemIndex) => (itemIndex === index ? { ...item, zone: e.target.value } : item)),
                })
              }
              placeholder="Zone"
            />
            <Input
              type="number"
              step="0.1"
              value={fee.fee}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  deliveryFees: draft.deliveryFees.map((item, itemIndex) => (itemIndex === index ? { ...item, fee: Number(e.target.value) } : item)),
                })
              }
              placeholder="Fee"
            />
            <Input
              type="number"
              step="0.1"
              value={fee.minOrder}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  deliveryFees: draft.deliveryFees.map((item, itemIndex) => (itemIndex === index ? { ...item, minOrder: Number(e.target.value) } : item)),
                })
              }
              placeholder="Min order"
            />
            <Button variant="outline" onClick={() => setDraft({ ...draft, deliveryFees: draft.deliveryFees.filter((_, itemIndex) => itemIndex !== index) })}>
              Entfernen
            </Button>
          </div>
        ))}
        <Button
          variant="outline"
          className="rounded-2xl"
          onClick={() =>
            setDraft({
              ...draft,
              deliveryFees: [...draft.deliveryFees, { id: createId('fee'), zone: '', fee: 0, minOrder: 0 }],
            })
          }
        >
          Delivery fee hinzufügen
        </Button>
      </div>
    ),
    partners: (
      <div className="grid gap-4 lg:grid-cols-3">
        {Object.entries(draft.thirdParty).map(([key, partner]) => (
          <Card key={key} className="rounded-[24px] border-slate-200 shadow-sm">
            <CardContent className="space-y-4 p-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-semibold text-[#132d39] capitalize">{key}</p>
                  <p className="text-sm text-slate-500">{partner.status}</p>
                </div>
                <input
                  type="checkbox"
                  checked={partner.enabled}
                  onChange={(e) => setDraft({ ...draft, thirdParty: { ...draft.thirdParty, [key]: { ...partner, enabled: e.target.checked } } })}
                />
              </div>
              <FormField label="Store / Restaurant ID">
                <Input
                  value={partner.storeId}
                  onChange={(e) => setDraft({ ...draft, thirdParty: { ...draft.thirdParty, [key]: { ...partner, storeId: e.target.value } } })}
                />
              </FormField>
              <FormField label="Webhook URL">
                <Input
                  value={partner.webhookUrl}
                  onChange={(e) => setDraft({ ...draft, thirdParty: { ...draft.thirdParty, [key]: { ...partner, webhookUrl: e.target.value } } })}
                />
              </FormField>
              <ToggleRow
                label="Auto accept"
                description="Bestellungen automatisch annehmen und direkt in die Kassa holen."
                checked={partner.autoAccept}
                onChange={(checked) => setDraft({ ...draft, thirdParty: { ...draft.thirdParty, [key]: { ...partner, autoAccept: checked } } })}
              />
            </CardContent>
          </Card>
        ))}
      </div>
    ),
    'article-mappings': (
      <div className="space-y-4">
        {categories.map((category) => {
          const current = draft.articleMappings.find((item) => item.categoryId === category.id) || { categoryId: category.id, partnerName: category.name };
          return (
            <div key={category.id} className="grid gap-3 md:grid-cols-[1fr_1fr]">
              <Input value={category.name} readOnly />
              <Input
                value={current.partnerName}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    articleMappings: [
                      ...draft.articleMappings.filter((item) => item.categoryId !== category.id),
                      { categoryId: category.id, partnerName: e.target.value },
                    ],
                  })
                }
                placeholder="Partner mapping"
              />
            </div>
          );
        })}
      </div>
    ),
    'cash-register': (
      <div className="grid gap-5 md:grid-cols-2">
        <FormField label="Terminal ID">
          <Input
            value={draft.cashRegisterConfig.terminalId}
            onChange={(e) => setDraft({ ...draft, cashRegisterConfig: { ...draft.cashRegisterConfig, terminalId: e.target.value } })}
          />
        </FormField>
        <FormField label="Kassen ID">
          <Input
            value={draft.cashRegisterConfig.kassenId}
            onChange={(e) => setDraft({ ...draft, cashRegisterConfig: { ...draft.cashRegisterConfig, kassenId: e.target.value } })}
          />
        </FormField>
        <div className="md:col-span-2 space-y-4">
          <ToggleRow
            label="Auto push to kitchen"
            description="Schickt akzeptierte Online-Bestellungen direkt an Küche / Monitore."
            checked={draft.cashRegisterConfig.autoPushToKitchen}
            onChange={(checked) => setDraft({ ...draft, cashRegisterConfig: { ...draft.cashRegisterConfig, autoPushToKitchen: checked } })}
          />
          <ToggleRow
            label="Auto sync completed orders"
            description="Markiert erfolgreich abgewickelte Bestellungen automatisch als synchronisiert."
            checked={draft.cashRegisterConfig.autoSyncCompletedOrders}
            onChange={(checked) => setDraft({ ...draft, cashRegisterConfig: { ...draft.cashRegisterConfig, autoSyncCompletedOrders: checked } })}
          />
        </div>
      </div>
    ),
    payment: (
      <div className="space-y-4">
        {Object.entries(draft.payment).map(([key, checked]) => (
          <ToggleRow
            key={key}
            label={key}
            description="Verfügbare Zahlungsart im Webshop, Pickup-Flow oder Partnerabgleich."
            checked={checked}
            onChange={(value) => setDraft({ ...draft, payment: { ...draft.payment, [key]: value } })}
          />
        ))}
      </div>
    ),
    'tables-qr': (
      <div className="grid gap-5 md:grid-cols-2">
        <ToggleRow
          label="Enable table selection"
          description="Für dine-in Orders wird im Webshop die Tischauswahl aktiviert."
          checked={draft.tablesQr.enableTableSelection}
          onChange={(checked) => setDraft({ ...draft, tablesQr: { ...draft.tablesQr, enableTableSelection: checked } })}
        />
        <div className="space-y-5">
          <FormField label="Default room">
            <select
              value={draft.tablesQr.defaultRoomId}
              onChange={(e) => setDraft({ ...draft, tablesQr: { ...draft.tablesQr, defaultRoomId: e.target.value } })}
              className="h-11 w-full rounded-xl border border-slate-200 px-4"
            >
              <option value="">Keine Vorauswahl</option>
              {rooms.map((room) => (
                <option key={room.id} value={room.id}>
                  {room.name}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="QR prefix">
            <Input
              value={draft.tablesQr.qrPrefix}
              onChange={(e) => setDraft({ ...draft, tablesQr: { ...draft.tablesQr, qrPrefix: e.target.value } })}
            />
          </FormField>
        </div>
      </div>
    ),
    'order-sources': (
      <div className="space-y-4">
        {draft.orderSources.map((source, index) => (
          <div key={source.id} className="grid gap-3 md:grid-cols-[0.9fr_1.2fr_auto]">
            <Input value={source.id} readOnly />
            <Input
              value={source.label}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  orderSources: draft.orderSources.map((item, itemIndex) => (itemIndex === index ? { ...item, label: e.target.value } : item)),
                })
              }
            />
            <label className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={source.enabled}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    orderSources: draft.orderSources.map((item, itemIndex) => (itemIndex === index ? { ...item, enabled: e.target.checked } : item)),
                  })
                }
              />
              Aktiv
            </label>
          </div>
        ))}
      </div>
    ),
    general: (
      <div className="space-y-4">
        <ToggleRow
          label="Allow guest checkout"
          description="Kunden müssen kein App-Konto anlegen, um im Webshop zu bestellen."
          checked={draft.general.allowGuestCheckout}
          onChange={(checked) => setDraft({ ...draft, general: { ...draft.general, allowGuestCheckout: checked } })}
        />
        <ToggleRow
          label="Print production slip"
          description="Erstellt Produktionszettel für Online-Bestellungen im Küchen- und Counter-Workflow."
          checked={draft.general.printProductionSlip}
          onChange={(checked) => setDraft({ ...draft, general: { ...draft.general, printProductionSlip: checked } })}
        />
        <ToggleRow
          label="Prompt for notes"
          description="Zeigt im Checkout zusätzliche Felder für Bemerkungen und Sonderwünsche an."
          checked={draft.general.notesPrompt}
          onChange={(checked) => setDraft({ ...draft, general: { ...draft.general, notesPrompt: checked } })}
        />
        <FormField label="Service fee percent">
          <Input
            type="number"
            value={draft.general.serviceFeePercent}
            onChange={(e) => setDraft({ ...draft, general: { ...draft.general, serviceFeePercent: Number(e.target.value) } })}
          />
        </FormField>
      </div>
    ),
  };

  return (
    <div className="pb-8">
      <PageHero title="Order Settings" description="Die komplette Bessa-Order-Settings-Struktur in einem Ort: Öffnungszeiten, Partner, QR, Payment und Cash-Register-Verhalten." actions={<SaveActions onSave={save} />} />
      <div className="grid gap-6 px-8 xl:grid-cols-[1.15fr_2.35fr]">
        <Card className="rounded-[32px] border-slate-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <SecondaryNav items={items} active={active} onChange={(value) => setSearchParams({ section: value })} />
          </CardContent>
        </Card>
        <Panel title={items.find((item) => item.id === active)?.label || 'Order Settings'} description="Angelehnt an die Live-Struktur aus dieKassa mit integriertem Bessa-Flow.">
          {contentBySection[active]}
        </Panel>
      </div>
    </div>
  );
}

export function AdminWebshop() {
  const { portalConfig, settings, isLoading, savePortalConfig } = useManagerPortalConfig();
  const [tables, setTables] = useState([]);
  const [draft, setDraft] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get('tab') || 'settings';

  useEffect(() => {
    getTables().then((data) => setTables(data || [])).catch(() => setTables([]));
  }, []);

  useEffect(() => {
    if (portalConfig?.webshop) {
      setDraft(portalConfig.webshop);
    }
  }, [portalConfig]);

  if (isLoading || !draft) return <LoadingView />;

  const items = [
    { id: 'settings', label: 'Settings', icon: Settings2 },
    { id: 'design', label: 'Design', icon: Palette },
    { id: 'qr', label: 'QR code', icon: QrCode },
  ];

  const effectiveSlug = sanitizeWebshopSlug(
    draft.urlSlug,
    settings?.company_name || portalConfig?.locationPresence?.locationName || 'FiscalPOS',
    settings?.company_city || '',
  );
  const currentUrl = buildWebshopUrl(effectiveSlug);

  const save = () => {
    savePortalConfig((prev) => ({
      ...prev,
      webshop: {
        ...draft,
        urlSlug: effectiveSlug,
        currentUrl,
      },
    }));
  };

  const copyLink = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Link kopiert');
    } catch {
      toast.error('Kopieren nicht möglich');
    }
  };

  const tableLinks = tables.slice(0, 6).map((table) => ({
    id: table.id,
    label: table.name,
    url: `${currentUrl}?table=${encodeURIComponent(table.name)}&prefix=${encodeURIComponent(portalConfig?.orderSettings?.tablesQr?.qrPrefix || 'TABLE')}`,
  }));

  return (
    <div className="pb-8">
      <PageHero title="Webshop" description="Konfiguration von URL, Verhalten, Design und QR-Verteilung für den integrierten Bessa-Webshop in der Kassa-Verwaltung." actions={<SaveActions onSave={save} />} />

      <div className="grid gap-6 px-8 xl:grid-cols-[1.05fr_2.45fr]">
        <Card className="rounded-[32px] border-slate-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <SecondaryNav items={items} active={active} onChange={(value) => setSearchParams({ tab: value })} />
          </CardContent>
        </Card>

        <Panel title={`Webshop ${active === 'design' ? 'design' : active === 'qr' ? 'QR code' : 'settings'}`} description="Die Oberfläche folgt direkt dem Bessa-Webshop-Manager: URL & address, behavior, design und QR.">
          {active === 'settings' && (
            <div className="space-y-8">
              <div className="rounded-[28px] border border-slate-200 p-5">
                <h3 className="text-2xl font-semibold text-[#132d39]">URL & address</h3>
                <p className="mt-2 text-sm text-slate-500">The URL where your customers reach the webshop.</p>
                <div className="mt-5 flex items-center justify-between gap-4 rounded-[24px] border border-slate-200 bg-slate-50 px-5 py-4">
                  <div>
                    <p className="text-sm text-slate-500">Current URL</p>
                    <p className="mt-1 font-mono text-lg text-[#71c545]">{currentUrl}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" onClick={() => window.open(currentUrl, '_blank')}>
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => copyLink(currentUrl)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-5">
                  <FormField label="URL name" hint="Letters, numbers, and dashes only.">
                    <Input value={draft.urlSlug} onChange={(e) => setDraft({ ...draft, urlSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} />
                  </FormField>
                </div>
                <div className="mt-4 rounded-[20px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  Changing the URL is a potentially risky action. Existing QR codes keep pointing to the old address.
                </div>
              </div>

              <div className="rounded-[28px] border border-slate-200 p-5">
                <h3 className="text-2xl font-semibold text-[#132d39]">Webshop behavior</h3>
                <p className="mt-2 text-sm text-slate-500">Choose which features and prompts customers see in the webshop.</p>
                <div className="mt-5 space-y-4">
                  <ToggleRow label="Enable table selection" description="Für dine-in orders wird die Tischauswahl eingeblendet." checked={draft.enableTableSelection} onChange={(checked) => setDraft({ ...draft, enableTableSelection: checked })} />
                  <ToggleRow label="Show newsletter opt-in" description="Optionaler Newsletter-Consent im Checkout." checked={draft.showNewsletterOptIn} onChange={(checked) => setDraft({ ...draft, showNewsletterOptIn: checked })} />
                  <ToggleRow label="Read-only mode" description="Zeigt Menü und Preise, blendet aber Cart und Checkout aus." checked={draft.readOnlyMode} onChange={(checked) => setDraft({ ...draft, readOnlyMode: checked })} />
                  <ToggleRow label="Skip app landing page" description="Öffnet den Webshop direkt ohne App-Promo-Zwischenseite." checked={draft.skipAppLandingPage} onChange={(checked) => setDraft({ ...draft, skipAppLandingPage: checked })} />
                </div>
              </div>
            </div>
          )}

          {active === 'design' && (
            <div className="grid gap-6 xl:grid-cols-[1fr_1fr]">
              <div className="space-y-5">
                <FormField label="Hero title">
                  <Input value={draft.heroTitle} onChange={(e) => setDraft({ ...draft, heroTitle: e.target.value })} />
                </FormField>
                <FormField label="Hero subtitle">
                  <Input value={draft.heroSubtitle} onChange={(e) => setDraft({ ...draft, heroSubtitle: e.target.value })} />
                </FormField>
                <FormField label="Primary color">
                  <Input value={draft.primaryColor} onChange={(e) => setDraft({ ...draft, primaryColor: e.target.value })} />
                </FormField>
                <FormField label="Accent color">
                  <Input value={draft.accentColor} onChange={(e) => setDraft({ ...draft, accentColor: e.target.value })} />
                </FormField>
                <FormField label="Surface color">
                  <Input value={draft.surfaceColor} onChange={(e) => setDraft({ ...draft, surfaceColor: e.target.value })} />
                </FormField>
              </div>
              <div className="rounded-[32px] border border-slate-200 p-6">
                <p className="text-sm font-medium text-slate-500">Preview</p>
                <div className="mt-4 overflow-hidden rounded-[28px] border border-slate-200 shadow-sm">
                  <div className="p-6" style={{ backgroundColor: draft.surfaceColor }}>
                    <div className="mb-6 inline-flex rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ backgroundColor: draft.primaryColor }}>
                      Powered by FiscalPOS
                    </div>
                    <h4 className="text-3xl font-bold" style={{ color: draft.accentColor }}>
                      {draft.heroTitle}
                    </h4>
                    <p className="mt-2 text-sm text-slate-600">{draft.heroSubtitle}</p>
                    <div className="mt-6 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-[20px] bg-white p-4 shadow-sm">
                        <p className="text-sm text-slate-500">Delivery</p>
                        <p className="mt-1 font-semibold text-[#132d39]">ETA 35 min</p>
                      </div>
                      <div className="rounded-[20px] bg-white p-4 shadow-sm">
                        <p className="text-sm text-slate-500">Pick up</p>
                        <p className="mt-1 font-semibold text-[#132d39]">Ready in 20 min</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {active === 'qr' && (
            <div className="space-y-6">
              <div className="rounded-[28px] border border-slate-200 p-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Public webshop QR target</p>
                    <p className="mt-1 font-mono text-lg text-[#71c545]">{currentUrl}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" onClick={() => window.open(currentUrl, '_blank')}>
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => copyLink(currentUrl)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {tableLinks.map((link) => (
                  <Card key={link.id} className="rounded-[24px] border-slate-200 shadow-sm">
                    <CardContent className="space-y-4 p-5">
                      <div className="flex h-28 items-center justify-center rounded-[20px] border border-dashed border-slate-200 bg-slate-50">
                        <QrCode className="h-12 w-12 text-[#132d39]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#132d39]">{link.label}</p>
                        <p className="mt-1 break-all text-xs text-slate-500">{link.url}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => copyLink(link.url)}>
                          <Copy className="mr-2 h-4 w-4" />
                          Copy
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => window.open(link.url, '_blank')}>
                          <ExternalLink className="mr-2 h-4 w-4" />
                          Open
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

export function AdminPickupDisplay() {
  const { portalConfig, isLoading, savePortalConfig } = useManagerPortalConfig();
  const [orders, setOrders] = useState([]);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    getOrders().then((data) => setOrders(data || [])).catch(() => setOrders([]));
  }, []);

  useEffect(() => {
    if (portalConfig?.pickupDisplay) {
      setDraft(portalConfig.pickupDisplay);
    }
  }, [portalConfig]);

  if (isLoading || !draft) return <LoadingView />;

  const previewOrders = orders.filter((order) => ['website', 'foodora', 'lieferando'].includes(order.order_source)).slice(0, 3);

  return (
    <div className="pb-8">
      <PageHero
        title="Pickup display"
        description="Konfiguriert den öffentlichen Abholschirm für Webshop- und Partner-Bestellungen direkt aus dem Manager Portal."
        actions={<SaveActions onSave={() => savePortalConfig((prev) => ({ ...prev, pickupDisplay: draft }))} />}
      />

      <div className="grid gap-6 px-8 xl:grid-cols-[0.95fr_1.35fr]">
        <Panel title="Display settings" description="Farben, Headline und das Anzeigeverhalten für den Abholschirm.">
          <FormField label="Headline">
            <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          </FormField>
          <FormField label="Subline">
            <Textarea value={draft.subtitle} onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })} />
          </FormField>
          <FormField label="Accent color">
            <Input value={draft.accentColor} onChange={(e) => setDraft({ ...draft, accentColor: e.target.value })} />
          </FormField>
          <FormField label="Auto clear (seconds)">
            <Input type="number" value={draft.autoClearSeconds} onChange={(e) => setDraft({ ...draft, autoClearSeconds: Number(e.target.value) })} />
          </FormField>
          <ToggleRow label="Show customer name" description="Erlaubt die Anzeige von Kundennamen neben der Bestellnummer." checked={draft.showCustomerName} onChange={(checked) => setDraft({ ...draft, showCustomerName: checked })} />
        </Panel>

        <Panel title="Live preview" description="Simuliert die Anzeige für fertiggestellte oder in Zubereitung befindliche Online-Bestellungen.">
          <div className="rounded-[32px] p-6 text-white" style={{ backgroundColor: '#132d39' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm uppercase tracking-[0.18em] text-white/60">Pickup display</p>
                <h3 className="mt-2 text-3xl font-bold">{draft.title}</h3>
                <p className="mt-2 text-white/70">{draft.subtitle}</p>
              </div>
              <div className="rounded-full px-4 py-2 text-sm font-semibold" style={{ backgroundColor: draft.accentColor }}>
                Live
              </div>
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {previewOrders.length ? previewOrders.map((order) => (
                <div key={order.id} className="rounded-[24px] bg-white/8 p-4 backdrop-blur-sm">
                  <p className="text-sm text-white/65">{sourceLabel(order.order_source)}</p>
                  <p className="mt-2 text-2xl font-bold">{order.order_number}</p>
                  {draft.showCustomerName ? <p className="mt-1 text-sm text-white/75">{order.customer_name || 'Pickup guest'}</p> : null}
                </div>
              )) : (
                <div className="rounded-[24px] border border-dashed border-white/15 bg-white/5 p-6 text-sm text-white/65 md:col-span-3">
                  Noch keine echten Online-Bestellungen im Vorschauzeitraum.
                </div>
              )}
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
}

const POS_ORDER_PANEL_WIDTH_OPTIONS = [
  { value: 'normal', label: 'Normal', description: 'Standardbreite (aktuell)' },
  { value: 'wide', label: 'Breit', description: 'Mehr Platz für Artikel & Modifier-Text' },
  { value: 'xwide', label: 'Extra breit', description: 'Maximaler Platz für den Bestellschein' },
];

const POS_THEME_FIELDS = [
  { key: 'headerColor', label: 'Header', hint: 'Dunkle Kopfzeile im Bonieren.' },
  { key: 'canvasColor', label: 'Arbeitsflaeche', hint: 'Hintergrund im Hauptbereich.' },
  { key: 'railColor', label: 'Kategorienleiste', hint: 'Linke Rail und Menu-Bar.' },
  { key: 'borderColor', label: 'Linien', hint: 'Trennlinien und Kachelrahmen.' },
  { key: 'accentColor', label: 'Akzent', hint: 'Aktive Highlights und gruene Akzente.' },
  { key: 'summaryColor', label: 'Summenbox', hint: 'Dunkle KPI-/Button-Flaechen im Header.' },
];

const GENERIC_MANAGER_CATEGORY_ICONS = new Set(['', 'Package', 'ClipboardList', 'LayoutGrid']);

function getManagerPosTheme(posTerminal) {
  return {
    ...POS_DEFAULT_TERMINAL_THEME,
    ...(posTerminal?.theme || {}),
  };
}

function getManagerCategoryAppearance(category, posTerminal) {
  const configured = posTerminal?.categoryAppearance?.[category.id] || {};
  const preset = findDefaultCategoryAppearance(category?.name) || {};
  const explicitIcon = !GENERIC_MANAGER_CATEGORY_ICONS.has(String(category?.icon || '').trim())
    ? String(category?.icon || '').trim()
    : '';

  return {
    icon: configured.icon || explicitIcon || preset.icon || 'UtensilsCrossed',
    color: configured.color || category?.color || preset.color || '#94a3b8',
    backgroundColor: configured.backgroundColor || preset.backgroundColor || '#eef2f4',
  };
}

function getManagerHomeShortcuts(posTerminal) {
  return normalizePosHomeShortcuts(posTerminal?.homeShortcuts);
}

export function AdminTerminals() {
  const { portalConfig, isLoading, savePortalConfig, saveSettingsPatch } = useManagerPortalConfig();
  const [draftTerminals, setDraftTerminals] = useState(null);
  const [draftCash, setDraftCash] = useState(null);
  const [draftPosTerminal, setDraftPosTerminal] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [draftFeaturedCategoryIds, setDraftFeaturedCategoryIds] = useState([]);
  const [draftFeaturedProductIds, setDraftFeaturedProductIds] = useState([]);

  useEffect(() => {
    if (portalConfig?.terminals) setDraftTerminals(portalConfig.terminals);
    if (portalConfig?.orderSettings?.cashRegisterConfig) setDraftCash(portalConfig.orderSettings.cashRegisterConfig);
    if (portalConfig?.posTerminal) setDraftPosTerminal(portalConfig.posTerminal);
    if (portalConfig?.menus) {
      setDraftFeaturedCategoryIds(portalConfig.menus.featuredCategoryIds || []);
      setDraftFeaturedProductIds(portalConfig.menus.featuredProductIds || []);
    }
  }, [portalConfig]);

  useEffect(() => {
    Promise.all([
      getCategories().catch(() => []),
      getProducts().catch(() => []),
    ])
      .then(([loadedCategories, loadedProducts]) => {
        setCategories(loadedCategories || []);
        setProducts(loadedProducts || []);
      })
      .catch(() => {
        setCategories([]);
        setProducts([]);
      });
  }, []);

  const orderedCategoryRows = useMemo(() => {
    const orderIds = draftPosTerminal?.categoryLayout?.order || [];
    const hiddenIds = new Set(draftPosTerminal?.categoryLayout?.hiddenIds || []);
    const orderIndex = new Map(orderIds.map((id, idx) => [id, idx]));
    return categories
      .map((cat, naturalIndex) => ({
        cat,
        hidden: hiddenIds.has(cat.id),
        rank: orderIndex.has(cat.id) ? orderIndex.get(cat.id) : 1000 + naturalIndex,
      }))
      .sort((a, b) => a.rank - b.rank);
  }, [categories, draftPosTerminal]);
  const posTheme = useMemo(() => getManagerPosTheme(draftPosTerminal), [draftPosTerminal]);
  const orderedProducts = useMemo(() => {
    const rankByCategoryId = new Map(orderedCategoryRows.map(({ cat }, index) => [cat.id, index]));
    return [...products].sort((left, right) => {
      const leftRank = rankByCategoryId.has(left.category_id) ? rankByCategoryId.get(left.category_id) : 9999;
      const rightRank = rankByCategoryId.has(right.category_id) ? rankByCategoryId.get(right.category_id) : 9999;
      if (leftRank !== rightRank) return leftRank - rightRank;
      return String(left.name || '').localeCompare(String(right.name || ''), 'de');
    });
  }, [orderedCategoryRows, products]);
  const homeShortcuts = useMemo(() => getManagerHomeShortcuts(draftPosTerminal), [draftPosTerminal]);

  const moveCategory = (categoryId, direction) => {
    const ids = orderedCategoryRows.map(({ cat }) => cat.id);
    const from = ids.indexOf(categoryId);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= ids.length) return;
    [ids[from], ids[to]] = [ids[to], ids[from]];
    setDraftPosTerminal({
      ...draftPosTerminal,
      categoryLayout: { ...draftPosTerminal.categoryLayout, order: ids },
    });
  };

  const toggleCategoryHidden = (categoryId) => {
    const hiddenIds = new Set(draftPosTerminal?.categoryLayout?.hiddenIds || []);
    if (hiddenIds.has(categoryId)) hiddenIds.delete(categoryId);
    else hiddenIds.add(categoryId);
    setDraftPosTerminal({
      ...draftPosTerminal,
      categoryLayout: { ...draftPosTerminal.categoryLayout, hiddenIds: Array.from(hiddenIds) },
    });
  };
  const updatePosThemeField = (field, value) => {
    setDraftPosTerminal({
      ...draftPosTerminal,
      theme: {
        ...posTheme,
        [field]: value,
      },
    });
  };
  const updateCategoryAppearance = (categoryId, patch) => {
    setDraftPosTerminal({
      ...draftPosTerminal,
      categoryAppearance: {
        ...(draftPosTerminal.categoryAppearance || {}),
        [categoryId]: {
          ...(draftPosTerminal.categoryAppearance?.[categoryId] || {}),
          ...patch,
        },
      },
    });
  };
  const resetCategoryAppearance = (category) => {
    const preset = findDefaultCategoryAppearance(category?.name) || null;
    const nextAppearance = { ...(draftPosTerminal.categoryAppearance || {}) };
    if (preset) {
      nextAppearance[category.id] = { ...preset };
    } else {
      delete nextAppearance[category.id];
    }
    setDraftPosTerminal({
      ...draftPosTerminal,
      categoryAppearance: nextAppearance,
    });
  };
  const toggleFeaturedCategory = (categoryId) => {
    setDraftFeaturedCategoryIds((current) => (
      current.includes(categoryId)
        ? current.filter((id) => id !== categoryId)
        : [...current, categoryId]
    ));
  };
  const toggleFeaturedProduct = (productId) => {
    setDraftFeaturedProductIds((current) => (
      current.includes(productId)
        ? current.filter((id) => id !== productId)
        : [...current, productId]
    ));
  };
  const updateHomeShortcut = (shortcutId, patch) => {
    const nextShortcuts = homeShortcuts.map((shortcut) => (
      shortcut.id === shortcutId ? { ...shortcut, ...patch } : shortcut
    ));
    setDraftPosTerminal({
      ...draftPosTerminal,
      homeShortcuts: normalizePosHomeShortcuts(nextShortcuts),
    });
  };
  const moveHomeShortcut = (shortcutId, direction) => {
    const nextShortcuts = [...homeShortcuts];
    const index = nextShortcuts.findIndex((shortcut) => shortcut.id === shortcutId);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= nextShortcuts.length) return;
    [nextShortcuts[index], nextShortcuts[nextIndex]] = [nextShortcuts[nextIndex], nextShortcuts[index]];
    setDraftPosTerminal({
      ...draftPosTerminal,
      homeShortcuts: normalizePosHomeShortcuts(
        nextShortcuts.map((shortcut, sortIndex) => ({ ...shortcut, sort_order: sortIndex })),
      ),
    });
  };
  const resetHomeShortcuts = () => {
    setDraftPosTerminal({
      ...draftPosTerminal,
      homeShortcuts: buildDefaultPosHomeShortcuts(),
    });
  };

  if (isLoading || !draftTerminals || !draftCash || !draftPosTerminal) return <LoadingView />;

  const save = async () => {
    savePortalConfig((prev) => ({
      ...prev,
      terminals: draftTerminals,
      posTerminal: draftPosTerminal,
      menus: {
        ...(prev.menus || {}),
        featuredCategoryIds: draftFeaturedCategoryIds,
        featuredProductIds: draftFeaturedProductIds,
      },
      orderSettings: {
        ...prev.orderSettings,
        cashRegisterConfig: draftCash,
      },
    }), '');
    await saveSettingsPatch(
      {
        terminal_id: draftCash.terminalId,
        kassen_id: draftCash.kassenId,
      },
      'Terminal IDs in der Kassa synchronisiert',
    );
    toast.success('Terminal- und Connector-Setup gespeichert');
  };

  return (
    <div className="pb-8">
      <PageHero title="Terminals" description="Verwaltet POS-, Webshop- und Delivery-Connectoren an einem Ort und orientiert sich am integrierten dieKassa/Bessa-Setup." actions={<SaveActions onSave={save} />} />
      <div className="grid gap-6 px-8 xl:grid-cols-[0.95fr_1.35fr]">
        <Panel title="Cash register identity" description="IDs und Verhalten, die auch in Order Settings und in der Kassa selbst verwendet werden.">
          <FormField label="Terminal ID">
            <Input value={draftCash.terminalId} onChange={(e) => setDraftCash({ ...draftCash, terminalId: e.target.value })} />
          </FormField>
          <FormField label="Kassen ID">
            <Input value={draftCash.kassenId} onChange={(e) => setDraftCash({ ...draftCash, kassenId: e.target.value })} />
          </FormField>
          <FormField label="Webhook secret">
            <Input value={draftTerminals.webhookSecret} onChange={(e) => setDraftTerminals({ ...draftTerminals, webhookSecret: e.target.value })} />
          </FormField>
          <FormField label="Bestellschein-Breite (Kassa-Terminal)">
            <div className="grid grid-cols-3 gap-2">
              {POS_ORDER_PANEL_WIDTH_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setDraftPosTerminal({ ...draftPosTerminal, orderPanelWidth: option.value })}
                  title={option.description}
                  className={`border px-3 py-2 text-sm font-semibold transition-colors ${
                    draftPosTerminal.orderPanelWidth === option.value
                      ? 'border-[#132d39] bg-[#132d39] text-white'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-slate-400">
              Steuert, wie viel Platz der Bestellschein (rechte Spalte) im POS-Terminal einnimmt.
            </p>
          </FormField>
        </Panel>
        <Panel title="Kategorien-Reihenfolge (Kassa)" description="Legt fest, welche Kategorien im linken Menü des POS-Terminals erscheinen und in welcher Reihenfolge.">
          {orderedCategoryRows.length === 0 ? (
            <p className="text-sm text-slate-400">Keine Kategorien vorhanden.</p>
          ) : (
            <div className="divide-y divide-slate-100 border border-slate-200">
              {orderedCategoryRows.map(({ cat, hidden }, index) => (
                <div key={cat.id} data-testid={`category-row-${cat.id}`} className={`flex items-center gap-2 px-3 py-2 ${hidden ? 'opacity-40' : ''}`}>
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => moveCategory(cat.id, -1)}
                      disabled={index === 0}
                      data-testid={`category-up-${cat.id}`}
                      className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveCategory(cat.id, 1)}
                      disabled={index === orderedCategoryRows.length - 1}
                      data-testid={`category-down-${cat.id}`}
                      className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: cat.color || '#64748b' }}
                  />
                  <span className="flex-1 truncate text-sm font-medium text-slate-700">{cat.name}</span>
                  <button
                    type="button"
                    data-testid={`category-toggle-${cat.id}`}
                    onClick={() => toggleCategoryHidden(cat.id)}
                    title={hidden ? 'In der Kassa einblenden' : 'In der Kassa ausblenden'}
                    className="text-slate-400 hover:text-slate-700"
                  >
                    {hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="mt-2 text-xs text-slate-400">
            Pfeile ändern die Reihenfolge, das Auge blendet eine Kategorie im Kassa-Menü aus (Produkte bleiben über die Suche weiter auffindbar).
          </p>
        </Panel>
        <Panel title="Bonieren Design" description="Farben und Icons fuer die POS-Ansicht. Die Defaults sind auf den hellen Gastronovi-inspirierten Look abgestimmt, bleiben aber im Admin anpassbar.">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {POS_THEME_FIELDS.map((field) => (
              <div key={field.key} className="border border-slate-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-[#132d39]">{field.label}</p>
                    <p className="mt-1 text-xs text-slate-500">{field.hint}</p>
                  </div>
                  <Input
                    type="color"
                    value={posTheme[field.key] || '#ffffff'}
                    onChange={(e) => updatePosThemeField(field.key, e.target.value)}
                    className="h-11 w-16 shrink-0 p-1"
                  />
                </div>
                <div className="mt-4 h-12 border border-slate-200" style={{ backgroundColor: posTheme[field.key] }} />
              </div>
            ))}
          </div>

          <div className="space-y-3">
            <div>
              <p className="font-semibold text-[#132d39]">Kategorien: Icon + Farben</p>
              <p className="mt-1 text-sm text-slate-500">Hier steuerst du den Badge, die Akzentfarbe und die helle Kachelfarbe fuer Rail und Hauptscreen.</p>
            </div>
            {orderedCategoryRows.map(({ cat, hidden }) => {
              const appearance = getManagerCategoryAppearance(cat, draftPosTerminal);
              return (
                <div key={`appearance-${cat.id}`} className={`border border-slate-200 p-4 ${hidden ? 'opacity-55' : ''}`}>
                  <div className="grid gap-4 xl:grid-cols-[minmax(0,1.3fr)_180px_140px_140px_auto] xl:items-end">
                    <div className="border border-slate-200 p-4" style={{ backgroundColor: appearance.backgroundColor }}>
                      <div className="flex items-center gap-4">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center text-center text-[11px] font-bold uppercase tracking-[0.08em] text-white" style={{ backgroundColor: appearance.color }}>
                          {appearance.icon.slice(0, 3)}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-xl font-semibold text-[#24323b]">{cat.name}</p>
                          <p className="mt-1 truncate text-xs uppercase tracking-[0.18em] text-slate-500">{appearance.icon}</p>
                        </div>
                      </div>
                    </div>

                    <FormField label="Icon">
                      <select
                        value={appearance.icon}
                        onChange={(e) => updateCategoryAppearance(cat.id, { icon: e.target.value })}
                        className="h-11 w-full border border-slate-200 bg-white px-3 text-sm text-slate-700"
                      >
                        {POS_CATEGORY_ICON_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </FormField>

                    <FormField label="Akzent">
                      <Input
                        type="color"
                        value={appearance.color}
                        onChange={(e) => updateCategoryAppearance(cat.id, { color: e.target.value })}
                        className="h-11 w-full p-1"
                      />
                    </FormField>

                    <FormField label="Kachel hell">
                      <Input
                        type="color"
                        value={appearance.backgroundColor}
                        onChange={(e) => updateCategoryAppearance(cat.id, { backgroundColor: e.target.value })}
                        className="h-11 w-full p-1"
                      />
                    </FormField>

                    <Button variant="outline" className="rounded-none" onClick={() => resetCategoryAppearance(cat)}>
                      Default
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
        <Panel title="Startscreen / Home page" description="Bearbeitet den klassischen Launcher mit festen, eckigen Kacheln wie im alten Kassenstil. Icons, Reihenfolge, Farben und Zielseite lassen sich direkt hier steuern.">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-[#132d39]">Live-Vorschau</p>
              <p className="mt-1 text-sm text-slate-500">Zeigt die POS-Startseite so, wie sie Bediener auf dem Gerät sehen.</p>
            </div>
            <Button variant="outline" className="rounded-none" onClick={resetHomeShortcuts}>
              Default wiederherstellen
            </Button>
          </div>

          <div className="border border-slate-200 bg-border p-3">
            <div className="grid grid-cols-2 gap-px border border-border bg-border">
              {homeShortcuts.filter((shortcut) => shortcut.enabled !== false).map((shortcut) => {
                const Icon = resolvePosHomeIcon(shortcut.icon);
                return (
                  <div
                    key={`preview-${shortcut.id}`}
                    className="flex min-h-[150px] flex-col items-center justify-center gap-4 px-4 py-5 text-center"
                    style={{ backgroundColor: shortcut.tileColor, color: shortcut.textColor }}
                  >
                    <Icon className="h-12 w-12" style={{ color: shortcut.accentColor }} strokeWidth={1.8} />
                    <div>
                      <p className="text-lg font-black leading-tight">{shortcut.title}</p>
                      <p className="mt-1 text-xs text-slate-500">{shortcut.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            {homeShortcuts.map((shortcut, index) => {
              const Icon = resolvePosHomeIcon(shortcut.icon);
              return (
                <div key={`home-shortcut-${shortcut.id}`} className={`border border-slate-200 p-4 ${shortcut.enabled === false ? 'opacity-55' : ''}`}>
                  <div className="grid gap-4 xl:grid-cols-[auto_minmax(0,1fr)_180px_180px_140px_140px_140px] xl:items-end">
                    <div className="flex items-start gap-2">
                      <div className="flex flex-col">
                        <button
                          type="button"
                          onClick={() => moveHomeShortcut(shortcut.id, -1)}
                          disabled={index === 0}
                          className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveHomeShortcut(shortcut.id, 1)}
                          disabled={index === homeShortcuts.length - 1}
                          className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>
                      </div>

                      <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
                        <input
                          type="checkbox"
                          checked={shortcut.enabled !== false}
                          onChange={(event) => updateHomeShortcut(shortcut.id, { enabled: event.target.checked })}
                          className="h-4 w-4 border-slate-300 text-[#71c545] focus:ring-[#71c545]"
                        />
                        Aktiv
                      </label>
                    </div>

                    <div className="border border-slate-200 p-3" style={{ backgroundColor: shortcut.tileColor }}>
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center" style={{ color: shortcut.accentColor }}>
                          <Icon className="h-8 w-8" strokeWidth={1.8} />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-lg font-black" style={{ color: shortcut.textColor }}>{shortcut.title}</p>
                          <p className="truncate text-xs text-slate-500">{shortcut.description}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <FormField label="Titel">
                        <Input value={shortcut.title} onChange={(e) => updateHomeShortcut(shortcut.id, { title: e.target.value })} className="rounded-none" />
                      </FormField>
                      <FormField label="Text">
                        <Input value={shortcut.description} onChange={(e) => updateHomeShortcut(shortcut.id, { description: e.target.value })} className="rounded-none" />
                      </FormField>
                    </div>

                    <FormField label="Zielseite">
                      <select
                        value={shortcut.path}
                        onChange={(e) => updateHomeShortcut(shortcut.id, { path: e.target.value })}
                        className="h-11 w-full border border-slate-200 bg-white px-3 text-sm text-slate-700"
                      >
                        {POS_HOME_ROUTE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </FormField>

                    <FormField label="Icon">
                      <select
                        value={shortcut.icon}
                        onChange={(e) => updateHomeShortcut(shortcut.id, { icon: e.target.value })}
                        className="h-11 w-full border border-slate-200 bg-white px-3 text-sm text-slate-700"
                      >
                        {POS_HOME_ICON_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </FormField>

                    <FormField label="Kachel">
                      <Input
                        type="color"
                        value={shortcut.tileColor}
                        onChange={(e) => updateHomeShortcut(shortcut.id, { tileColor: e.target.value })}
                        className="h-11 w-full p-1 rounded-none"
                      />
                    </FormField>

                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                      <FormField label="Icon-Farbe">
                        <Input
                          type="color"
                          value={shortcut.accentColor}
                          onChange={(e) => updateHomeShortcut(shortcut.id, { accentColor: e.target.value })}
                          className="h-11 w-full p-1 rounded-none"
                        />
                      </FormField>
                      <FormField label="Text-Farbe">
                        <Input
                          type="color"
                          value={shortcut.textColor}
                          onChange={(e) => updateHomeShortcut(shortcut.id, { textColor: e.target.value })}
                          className="h-11 w-full p-1 rounded-none"
                        />
                      </FormField>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
        <Panel title="Hauptscreen in Bonieren" description="Bestimmte Kategorien oder Produkte koennen direkt am Startscreen gespeichert werden, aehnlich zu den Favoriten-/Schnellzugriffen in modernen POS-Systemen.">
          <div className="grid gap-6 xl:grid-cols-2">
            <div className="space-y-3">
              <div>
                <p className="font-semibold text-[#132d39]">Gespeicherte Kategorien</p>
                <p className="mt-1 text-sm text-slate-500">Diese Kategorien erscheinen zusaetzlich im oberen Hauptscreen-Bereich.</p>
              </div>
              <div className="max-h-[28rem] space-y-2 overflow-y-auto pr-1">
                {orderedCategoryRows.map(({ cat }) => {
                  const appearance = getManagerCategoryAppearance(cat, draftPosTerminal);
                  return (
                    <label key={`featured-category-${cat.id}`} className="flex items-center gap-3 border border-slate-200 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={draftFeaturedCategoryIds.includes(cat.id)}
                        onChange={() => toggleFeaturedCategory(cat.id)}
                        className="h-4 w-4 rounded border-slate-300 text-[#71c545] focus:ring-[#71c545]"
                      />
                      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center text-[11px] font-bold uppercase tracking-[0.08em] text-white" style={{ backgroundColor: appearance.color }}>
                        {appearance.icon.slice(0, 2)}
                      </span>
                      <span className="min-w-0 flex-1 truncate font-medium text-slate-700">{cat.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <p className="font-semibold text-[#132d39]">Gespeicherte Produkte</p>
                <p className="mt-1 text-sm text-slate-500">Diese Artikel erscheinen ebenfalls direkt auf dem Hauptscreen als Schnellartikel.</p>
              </div>
              <div className="max-h-[28rem] space-y-2 overflow-y-auto pr-1">
                {orderedProducts.map((product) => {
                  const category = categories.find((item) => item.id === product.category_id) || null;
                  return (
                    <label key={`featured-product-${product.id}`} className="flex items-center gap-3 border border-slate-200 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={draftFeaturedProductIds.includes(product.id)}
                        onChange={() => toggleFeaturedProduct(product.id)}
                        className="h-4 w-4 rounded border-slate-300 text-[#71c545] focus:ring-[#71c545]"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-slate-700">{product.name}</p>
                        <p className="mt-1 truncate text-xs text-slate-500">
                          {(category?.name || 'Ohne Kategorie')} · {formatMoney(product.price)}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </Panel>
        <Panel title="Connected devices" description="Connector-Karten für Webshop, Foodora und Lieferando direkt im Manager Portal.">
          <div className="grid gap-4 md:grid-cols-2">
            {draftTerminals.devices.map((device, index) => (
              <Card key={device.id} className="border-slate-200 shadow-sm rounded-none">
                <CardContent className="space-y-3 p-5">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="font-semibold text-[#132d39]">{device.name}</p>
                      <p className="text-sm text-slate-500">{device.type}</p>
                    </div>
                    <span className="bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">{device.status}</span>
                  </div>
                  <FormField label="Name">
                    <Input
                      value={device.name}
                      onChange={(e) =>
                        setDraftTerminals({
                          ...draftTerminals,
                          devices: draftTerminals.devices.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, name: e.target.value } : item,
                          ),
                        })
                      }
                    />
                  </FormField>
                </CardContent>
              </Card>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

export function AdminLoyaltyCenter({ section = 'cards' }) {
  const { portalConfig, isLoading, savePortalConfig } = useManagerPortalConfig();
  const [customers, setCustomers] = useState([]);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    getCustomers().then((data) => setCustomers(data || [])).catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    if (portalConfig?.loyalty) setDraft(portalConfig.loyalty);
  }, [portalConfig]);

  if (isLoading || !draft) return <LoadingView />;

  const issuedCards = draft.issuedCards.length
    ? draft.issuedCards
    : customers.slice(0, 5).map((customer, index) => ({
        id: customer.id,
        customer: customer.name,
        stamps: (index + 2) % (draft.stampGoal || 10),
      }));

  const save = () => savePortalConfig((prev) => ({ ...prev, loyalty: draft }));

  return (
    <div className="pb-8">
      <PageHero title={section === 'cards' ? 'Loyalty Cards' : section === 'issued' ? 'Issued Cards' : section === 'coupons' ? 'Coupons' : 'Transactions'} description="Die Loyalty- und Gutscheinstrecke nach dem Online-Ordering-Block, wie in dieKassa sichtbar." actions={<SaveActions onSave={save} />} />
      <div className="px-8">
        {section === 'cards' && (
          <Panel title="Card program" description="Stempelziel und Belohnungslogik für App- und Loyalty-Karten.">
            <div className="grid gap-5 md:grid-cols-2">
              <ToggleRow label="Enable loyalty cards" description="Schaltet das Kartenprogramm systemweit aktiv." checked={draft.cardsEnabled} onChange={(checked) => setDraft({ ...draft, cardsEnabled: checked })} />
              <FormField label="Stamp goal">
                <Input type="number" value={draft.stampGoal} onChange={(e) => setDraft({ ...draft, stampGoal: Number(e.target.value) })} />
              </FormField>
              <div className="md:col-span-2">
                <FormField label="Reward title">
                  <Input value={draft.rewardTitle} onChange={(e) => setDraft({ ...draft, rewardTitle: e.target.value })} />
                </FormField>
              </div>
            </div>
          </Panel>
        )}

        {section === 'issued' && (
          <Panel title="Issued cards" description="Bestehende Loyalty-Karten aus dem CRM-Kontext, sofort sichtbar im Portal.">
            <div className="space-y-3">
              {issuedCards.map((card) => (
                <div key={card.id} className="flex items-center justify-between rounded-[24px] border border-slate-200 px-5 py-4">
                  <div>
                    <p className="font-semibold text-[#132d39]">{card.customer}</p>
                    <p className="text-sm text-slate-500">{card.stamps}/{draft.stampGoal} Stempel</p>
                  </div>
                  <div className="rounded-full bg-[#d8ebe0] px-3 py-1 text-xs font-semibold text-[#132d39]">Aktiv</div>
                </div>
              ))}
            </div>
          </Panel>
        )}

        {section === 'coupons' && (
          <Panel title="Coupon templates" description="Coupons, Gutscheinaktionen und Loyalty-Coupons in einer einheitlichen Oberfläche.">
            <div className="space-y-4">
              {draft.coupons.map((coupon, index) => (
                <div key={coupon.id} className="grid gap-3 md:grid-cols-[1fr_0.8fr_auto]">
                  <Input
                    value={coupon.code}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        coupons: draft.coupons.map((item, itemIndex) => (itemIndex === index ? { ...item, code: e.target.value } : item)),
                      })
                    }
                  />
                  <Input
                    value={coupon.value}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        coupons: draft.coupons.map((item, itemIndex) => (itemIndex === index ? { ...item, value: e.target.value } : item)),
                      })
                    }
                  />
                  <label className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={coupon.active}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          coupons: draft.coupons.map((item, itemIndex) => (itemIndex === index ? { ...item, active: e.target.checked } : item)),
                        })
                      }
                    />
                    Aktiv
                  </label>
                </div>
              ))}
              <Button
                variant="outline"
                className="rounded-2xl"
                onClick={() =>
                  setDraft({
                    ...draft,
                    coupons: [...draft.coupons, { id: createId('coupon'), code: 'NEWCODE', value: '5% Rabatt', active: true }],
                  })
                }
              >
                Coupon hinzufügen
              </Button>
            </div>
          </Panel>
        )}

        {section === 'transactions' && (
          <Panel title="Loyalty transactions" description="Ein einfacher Manager-Überblick über Loyalty- und Gutscheinbewegungen.">
            <div className="space-y-3">
              {(draft.transactions.length ? draft.transactions : issuedCards.map((card, index) => ({
                id: card.id,
                customer: card.customer,
                action: index % 2 === 0 ? 'Stamp earned' : 'Coupon redeemed',
                created_at: new Date().toISOString(),
              }))).map((item) => (
                <div key={item.id} className="flex items-center justify-between rounded-[24px] border border-slate-200 px-5 py-4">
                  <div>
                    <p className="font-semibold text-[#132d39]">{item.customer}</p>
                    <p className="text-sm text-slate-500">{item.action}</p>
                  </div>
                  <p className="text-sm text-slate-500">{formatDateTime(item.created_at)}</p>
                </div>
              ))}
            </div>
          </Panel>
        )}
      </div>
    </div>
  );
}

export function AdminNotifications() {
  const { portalConfig, isLoading, savePortalConfig } = useManagerPortalConfig();
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [draftNotification, setDraftNotification] = useState({
    title: '',
    body: '',
    audience: 'all',
  });

  if (isLoading || !portalConfig) return <LoadingView />;

  const notifications = portalConfig.notifications || [];
  const filtered = notifications.filter((notification) =>
    `${notification.title} ${notification.body}`.toLowerCase().includes(search.toLowerCase()),
  );

  const createNotification = () => {
    const next = {
      id: createId('notification'),
      title: draftNotification.title,
      body: draftNotification.body,
      audience: draftNotification.audience,
      createdAt: new Date().toISOString(),
    };
    savePortalConfig((prev) => ({
      ...prev,
      notifications: [next, ...(prev.notifications || [])],
    }));
    setDraftNotification({ title: '', body: '', audience: 'all' });
    setIsModalOpen(false);
  };

  return (
    <div className="pb-8">
      <PageHero
        title="Notifications"
        description="Push- und App-Benachrichtigungen, wie in der integrierten Bessa-Ansicht von dieKassa."
        actions={
          <Button onClick={() => setIsModalOpen(true)} className="rounded-2xl bg-[#71c545] text-white hover:bg-[#63b13b]">
            Neue Benachrichtigung
          </Button>
        }
      />

      <div className="px-8">
        <Panel title="Filter" description="Die Filterleiste orientiert sich an der Live-Ansicht mit Search und Active filters.">
          <div className="grid gap-4 lg:grid-cols-[0.8fr_1.2fr]">
            <Button variant="outline" className="justify-start rounded-2xl">Close filter</Button>
            <div className="rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-500">
              <span className="font-semibold text-[#132d39]">ACTIVE FILTERS</span> No filters
            </div>
          </div>
          <div className="relative pt-2">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" placeholder="Search" />
          </div>

          {filtered.length === 0 ? (
            <div className="rounded-[28px] border border-dashed border-slate-200 px-6 py-16 text-center">
              <Bell className="mx-auto h-10 w-10 text-slate-300" />
              <h3 className="mt-5 text-4xl font-bold text-[#132d39]">No notifications yet</h3>
              <p className="mt-3 text-slate-500">Inform your app users about new actions, stamps, or opening hours.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((notification) => (
                <div key={notification.id} className="rounded-[24px] border border-slate-200 px-5 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-semibold text-[#132d39]">{notification.title}</p>
                      <p className="mt-2 text-sm text-slate-500">{notification.body}</p>
                    </div>
                    <div className="text-right text-xs text-slate-500">
                      <p>{notification.audience}</p>
                      <p className="mt-1">{formatDateTime(notification.createdAt)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-xl rounded-[32px] bg-white shadow-2xl">
            <div className="border-b border-slate-100 px-6 py-5">
              <h2 className="text-2xl font-bold text-[#132d39]">New notification</h2>
            </div>
            <div className="space-y-5 px-6 py-6">
              <FormField label="Titel">
                <Input value={draftNotification.title} onChange={(e) => setDraftNotification({ ...draftNotification, title: e.target.value })} />
              </FormField>
              <FormField label="Nachricht">
                <Textarea value={draftNotification.body} onChange={(e) => setDraftNotification({ ...draftNotification, body: e.target.value })} className="min-h-[140px]" />
              </FormField>
              <FormField label="Audience">
                <select value={draftNotification.audience} onChange={(e) => setDraftNotification({ ...draftNotification, audience: e.target.value })} className="h-11 w-full rounded-xl border border-slate-200 px-4">
                  <option value="all">All users</option>
                  <option value="loyalty">Loyalty customers</option>
                  <option value="webshop">Webshop customers</option>
                </select>
              </FormField>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-5">
              <Button variant="ghost" onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button onClick={createNotification} className="rounded-2xl bg-[#71c545] text-white hover:bg-[#63b13b]">
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
